import { motion, AnimatePresence } from "framer-motion";
import { 
  ClipboardList, 
  AlertTriangle, 
  Pill, 
  Calendar, 
  Smartphone,
  Timer,
  CheckCircle2,
  TrendingUp,
  Zap,
  FileText
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const heroImage = "/assets/cards/abstract_digital_medical_records_preparation_background.png";
const appScreen = "/assets/cards/medical_dashboard_showing_patient_history_sections.png";

export default function ChartPrepCard() {
  const features = [
    {
      icon: ClipboardList,
      title: "AI-Curated Patient Briefs",
      desc: "Everything relevant—history, meds, allergies, pending labs—ready before you walk in"
    },
    {
      icon: AlertTriangle,
      title: "Proactive Risk Alerts",
      desc: "Care gaps, overdue screenings, and high-risk indicators surfaced automatically"
    },
    {
      icon: Pill,
      title: "Smart Med Reconciliation",
      desc: "Cross-referenced medication lists with interaction and duplicate warnings"
    },
    {
      icon: Calendar,
      title: "Appointment Context Aware",
      desc: "Prep content tailored to visit type: follow-up, new complaint, wellness check"
    },
    {
      icon: Smartphone,
      title: "Pre-Visit Patient Outreach",
      desc: "Automated SMS/email reminders with intake links and arrival instructions"
    }
  ];

  const kpis = [
    { label: "Prep Time", value: "-85%", icon: Timer, color: "text-emerald-400" },
    { label: "Chart Completeness", value: "95%", icon: CheckCircle2, color: "text-blue-400" },
    { label: "Care Gap Closure", value: "+40%", icon: TrendingUp, color: "text-pink-400" },
    { label: "Provider Readiness", value: "99%", icon: Zap, color: "text-amber-400" },
  ];

  const [currentKpi, setCurrentKpi] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentKpi((prev) => (prev + 1) % kpis.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [kpis.length]);

  return (
    <div className="w-full h-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl grid lg:grid-cols-2 gap-0 h-[700px]"
      >
        {/* Left Column: Content */}
        <div className="p-6 md:p-8 flex flex-col justify-start relative z-10 overflow-hidden">
          <div className="mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-sm font-bold uppercase tracking-wider w-fit" style={{ color: PRIMARY_BLUE }}>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: PRIMARY_BLUE }}></span>
            </span>
            ChartPrep AI
          </div>
          
          <h1 className="text-3xl md:text-4xl font-normal mb-2 tracking-tight" style={{ color: PRIMARY_BLUE }}>
            ChartPrep Automation
          </h1>
          
          <h2 className="text-lg md:text-xl font-medium mb-6" style={{ color: `${PRIMARY_BLUE}cc` }}>
            Pre-Visit Intelligence, Zero Prep Time
          </h2>
          
          <p className="text-lg text-slate-500 mb-8 italic border-l-4 pl-5" style={{ borderColor: `${ACCENT_COLOR}80` }}>
            "AI that reads ahead so providers can focus on what's in front of them."
          </p>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0">
                  <div 
                    className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:text-white"
                    style={{ 
                      '--hover-bg': PRIMARY_BLUE 
                    } as React.CSSProperties}
                  >
                    <feature.icon className="w-6 h-6 transition-colors" style={{ color: ACCENT_COLOR }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg transition-colors" style={{ color: PRIMARY_BLUE }}>
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Visuals */}
        <div className="relative h-full overflow-hidden bg-slate-900 flex items-center justify-center">
          
          {/* Background Layer */}
          <div className="absolute inset-0 z-0">
             <img 
              src={heroImage} 
              alt="Background Flow" 
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-slate-900/30 mix-blend-multiply" />
          </div>

          {/* Floating Dashboard Screen - iPad in Landscape */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, rotateX: 40, rotateY: 0, rotateZ: 0 }}
            animate={{ opacity: 1, scale: 1, rotateX: 50, rotateY: 0, rotateZ: -5 }}
            transition={{ delay: 0.4, duration: 1, ease: "easeOut" }}
            style={{ transformStyle: "preserve-3d" }}
            className="relative z-10 w-[85%] max-w-lg aspect-[4/3] rounded-[1.5rem] border-[12px] border-slate-800 bg-slate-800 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] overflow-hidden -mt-8"
          >
             {/* iPad Camera Notch - Side (Landscape) */}
             <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-slate-800 rounded-l-md z-30" />
             
             {/* Screen Content */}
             <div className="relative w-full h-full bg-white overflow-hidden rounded-[1rem]">
               <img 
                src={appScreen} 
                alt="ChartPrep Dashboard" 
                className="w-full h-full object-cover"
              />
             </div>
             
             {/* Screen Reflections */}
             <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/20 pointer-events-none z-20 rounded-[1rem]" />
             <div className="absolute inset-0 bg-gradient-to-b from-black/5 to-transparent pointer-events-none z-20 rounded-[1rem]" />
          </motion.div>

          {/* Floating Feature Context Cards */}
          <div className="absolute inset-0 z-20 pointer-events-none">
             
             {/* Patient Brief Alert - Top Left */}
             <motion.div
               animate={{ y: [0, -8, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0 }}
               className="absolute top-[8%] left-[6%] bg-white/95 backdrop-blur text-slate-800 p-4 rounded-xl shadow-xl flex items-center gap-3 max-w-[200px]"
             >
               <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                 <FileText className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-slate-500 uppercase">Brief Ready</div>
                 <div className="text-base font-bold text-slate-800">John Doe • Follow-up</div>
               </div>
             </motion.div>

             {/* Risk Alert - Top Right */}
             <motion.div
               animate={{ y: [0, 10, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
               className="absolute top-[12%] right-[4%] bg-amber-50/95 backdrop-blur border border-amber-200 text-slate-800 p-4 rounded-xl shadow-xl flex items-center gap-3 max-w-[210px]"
             >
               <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                 <AlertTriangle className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-amber-600 uppercase">Risk Alert</div>
                 <div className="text-base font-bold text-slate-800">Overdue HbA1c</div>
               </div>
             </motion.div>

             {/* Med Check - Bottom Left */}
             <motion.div
               animate={{ y: [0, -6, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
               className="absolute bottom-[30%] left-[4%] bg-emerald-50/95 backdrop-blur border border-emerald-200 text-slate-800 p-4 rounded-xl shadow-xl flex items-center gap-3 max-w-[200px]"
             >
               <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                 <CheckCircle2 className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-emerald-600 uppercase">Medication</div>
                 <div className="text-base font-bold text-slate-800">Reconciled</div>
               </div>
             </motion.div>

          </div>

          {/* Floating UI Cards (Context) - KPI Carousel */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 w-72 hidden md:block">
            <div className="relative h-20 perspective-1000">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentKpi}
                  initial={{ opacity: 0, y: 15, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -15, rotateX: -10 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                  className="absolute inset-0 backdrop-blur-md border border-white/20 px-4 py-3 rounded-xl flex items-center gap-4 overflow-hidden"
                  style={{
                    backgroundColor: `${PRIMARY_BLUE}e6`,
                    boxShadow: `0 10px 30px -10px ${PRIMARY_BLUE}99`
                  }}
                >
                   {/* Glare Effect */}
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                  <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 relative z-10">
                    {(() => {
                      const Icon = kpis[currentKpi].icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="text-xs font-bold text-white/70 uppercase tracking-wider leading-tight">
                      {kpis[currentKpi].label}
                    </div>
                    <div className="text-2xl font-bold text-white drop-shadow-sm">
                      {kpis[currentKpi].value}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
              {kpis.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-300 shadow-sm ${
                    idx === currentKpi ? "w-4" : "w-1.5 bg-slate-300/50"
                  }`}
                  style={{
                    backgroundColor: idx === currentKpi ? PRIMARY_BLUE : undefined
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

